import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { RiskAssessmentResultControlType } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import type { InsertRiskAssessmentResultsMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export type InsertRiskAssessmentResultInput = {
  RiskIds: string[];
  ControlType: RiskAssessmentResultControlType;
  Rating?: number | null;
  Likelihood?: number | null;
  Impact?: number | null;
  Impacts?: { Label: string; Value: number }[] | null;
  AssessmentId?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
  TestDate?: string | null;
  Rationale?: string | null;
};

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
function mapTrpcResponseToGraphQL(trpcData: {
  Ids: string[];
}): InsertRiskAssessmentResultsMutation {
  return {
    insertChildRiskAssessmentResult: {
      __typename: 'InsertChildRiskAssessmentResultOutput',
      Ids: trpcData.Ids,
    },
  };
}

export const useInsertRiskAssessmentResultTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.riskAssessmentResult.insert.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.assessment.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.assessment.resultsRegister.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.assessment.riskAssessmentResultsByRiskId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.risk.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.risk.scores.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.risk.riskScoresByRiskId.queryKey(),
        });
      },
    }),
  });

  return {
    insertRiskAssessmentResult: async (
      variables: InsertRiskAssessmentResultInput
    ): Promise<InsertRiskAssessmentResultsMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
